<template lang="pug">
    v-dialog(:value="value" max-width="560" @input="$emit('input', $event)" @keydown.esc="$emit('input', false)")
        v-card
            v-card-title.text-subtitle-1.font-weight-bold.pb-0 {{ $t('WORK_AGENT.PERMISSION_SETTINGS') }}{{ entityName ? ` - ${entityName}` : '' }}
            v-card-text.pb-0
                //- Subtitle: entity type + owner
                .caption.grey--text.mb-2
                    span {{ entityTypeLabel }}
                    span(v-if="entityOwner") ・{{ $t('WORK_AGENT.OWNER_LABEL') }}：{{ entityOwner }}
                //- Info strip: only owner can manage (Instance)
                .info-strip.mb-3(v-if="entityType === 'INSTANCE'")
                    v-icon.mr-1(size="14" color="#757575") mdi-pin-outline
                    span {{ $t('WORK_AGENT.INSTANCE_OWNER_ONLY_INFO') }}
                //- Visibility label
                .font-weight-bold.body-2.mb-2 {{ $t('WORK_AGENT.VISIBILITY_LABEL') }}
                //- Visibility radio cards
                v-row.mb-2(no-gutters)
                    v-col.pr-1(cols="6")
                        .visibility-card(@click="isPublic = false" :class="{ 'visibility-card--active': !isPublic }")
                            .d-flex.align-center.mb-1
                                .radio-circle.mr-2(:class="{ 'radio-circle--active': !isPublic }")
                                    .radio-dot(v-if="!isPublic")
                                img.mr-1(:src="require('@/assets/images/template_private_icon.svg')" width="16" height="16")
                                .body-2.font-weight-bold(:style="{ color: !isPublic ? '#111' : '#555' }") {{ $t('WORK_AGENT.VISIBILITY_PRIVATE') }}
                            .caption.grey--text.pl-6 {{ $t('WORK_AGENT.PRIVATE_DESCRIPTION') }}
                    v-col.pl-1(cols="6")
                        .visibility-card(@click="isPublic = true" :class="{ 'visibility-card--active': isPublic }")
                            .d-flex.align-center.mb-1
                                .radio-circle.mr-2(:class="{ 'radio-circle--active': isPublic }")
                                    .radio-dot(v-if="isPublic")
                                img.mr-1(:src="require('@/assets/images/template_public_icon.svg')" width="16" height="16")
                                .body-2.font-weight-bold(:style="{ color: isPublic ? '#111' : '#555' }") {{ $t('WORK_AGENT.VISIBILITY_PUBLIC') }}
                            .caption.grey--text.pl-6 {{ $t('WORK_AGENT.PUBLIC_DESCRIPTION') }}
                //- Public warning
                v-alert.mb-3(
                    v-if="isPublic"
                    dense text outlined
                    color="#14C6DE"
                    icon="mdi-information-outline"
                    style="font-size:12px;"
                )
                    | {{ entityType === 'TEMPLATE' ? $t('WORK_AGENT.PUBLIC_WARNING_TEMPLATE') : $t('WORK_AGENT.PUBLIC_WARNING_INSTANCE') }}
                //- User & Group sharing — hidden when public
                template(v-if="!isPublic")
                    .font-weight-bold.body-2.mb-1 {{ $t('WORK_AGENT.SHARE_TO_USER') }}
                    v-autocomplete.mb-1.share-input(
                        v-model="selectedUsers"
                        :items="selectableUsers"
                        item-value="id"
                        item-text="name"
                        :filter="filterUsers"
                        multiple
                        chips
                        hide-details
                        dense
                        solo
                        flat
                        :loading="loadingData"
                        :placeholder="$t('WORK_AGENT.SEARCH_USER_PLACEHOLDER')"
                        :no-data-text="$t('GENERAL.NO_DATA')"
                        :menu-props="{ contentClass: 'share-menu', offsetY: true }"
                    )
                        template(v-slot:selection="{ item }")
                            v-chip.my-1.share-chip(small close close-icon="mdi-close" @click:close="removeUser(item)")
                                .user-avatar.mr-1(:style="{ background: avatarColor(item.id) }") {{ userInitial(item.name) }}
                                span {{ item.name }}
                        template(v-slot:prepend-item)
                            v-list-item(ripple @mousedown.prevent @click="toggleAllUsers")
                                v-list-item-action
                                    v-icon(:color="selectedUsers.length > 0 ? '#14C6DE' : '#BDBDBD'") {{ selectAllUsersIcon }}
                                v-list-item-content
                                    v-list-item-title.font-weight-medium {{ $t('WORK_AGENT.SELECT_ALL') }}
                            v-divider
                        template(v-slot:item="{ item, on, attrs }")
                            v-list-item(v-bind="attrs" v-on="on")
                                v-list-item-action
                                    v-icon(:color="attrs.inputValue ? '#14C6DE' : '#BDBDBD'") {{ attrs.inputValue ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                                .user-item-avatar.mr-2(:style="{ background: avatarColor(item.id) }") {{ userInitial(item.name) }}
                                v-list-item-content
                                    v-list-item-title.body-2 {{ item.name }}
                                    v-list-item-subtitle.caption(v-if="item.email") {{ item.email }}
                    .caption.grey--text.mb-3 {{ entityType === 'TEMPLATE' ? $t('WORK_AGENT.SHARE_USER_DESC_TEMPLATE') : $t('WORK_AGENT.SHARE_USER_DESC_INSTANCE') }}
                    .font-weight-bold.body-2.mb-1 {{ $t('WORK_AGENT.SHARE_TO_GROUP') }}
                    v-autocomplete.mb-1.share-input(
                        v-model="selectedGroups"
                        :items="groupList"
                        item-value="id"
                        item-text="name"
                        multiple
                        chips
                        hide-details
                        dense
                        solo
                        flat
                        :loading="loadingData"
                        :placeholder="$t('WORK_AGENT.SEARCH_GROUP_PLACEHOLDER')"
                        :no-data-text="$t('GENERAL.NO_DATA')"
                        :menu-props="{ contentClass: 'share-menu', offsetY: true }"
                    )
                        template(v-slot:selection="{ item }")
                            v-chip.my-1.share-chip(small close close-icon="mdi-close" @click:close="removeGroup(item)")
                                v-icon.mr-1(size="13" color="#9E9E9E") mdi-sitemap
                                span {{ item.memberCount !== null ? `${item.name} ${item.memberCount} 位成員` : item.name }}
                        template(v-slot:prepend-item)
                            v-list-item(ripple @mousedown.prevent @click="toggleAllGroups")
                                v-list-item-action
                                    v-icon(:color="selectedGroups.length > 0 ? '#14C6DE' : '#BDBDBD'") {{ selectAllGroupsIcon }}
                                v-list-item-content
                                    v-list-item-title.font-weight-medium {{ $t('WORK_AGENT.SELECT_ALL') }}
                            v-divider
                        template(v-slot:item="{ item, on, attrs }")
                            v-list-item(v-bind="attrs" v-on="on")
                                v-list-item-action
                                    v-icon(:color="attrs.inputValue ? '#14C6DE' : '#BDBDBD'") {{ attrs.inputValue ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                                v-icon.mr-2(size="16" color="#9E9E9E") mdi-sitemap
                                v-list-item-content
                                    v-list-item-title.body-2 {{ item.name }}
                                    v-list-item-subtitle.caption(v-if="item.memberCount !== null") {{ item.memberCount }} 位成員
                    .caption.grey--text.mb-1 {{ $t('WORK_AGENT.SHARE_GROUP_DESC') }}
            v-card-actions.pt-3
                .caption.d-flex.align-center(v-if="saveState === 'saving'" style="color:#999;")
                    v-progress-circular.mr-1(indeterminate size="12" width="2" color="#14C6DE")
                    span {{ $t('WORK_AGENT.SAVING') }}
                .caption.d-flex.align-center(v-else-if="saveState === 'saved'" style="color:#4CAF50;")
                    v-icon.mr-1(size="14" color="#4CAF50") mdi-check-circle-outline
                    span {{ $t('WORK_AGENT.SAVED') }}
                v-spacer
                v-btn(width="80" color="#14C6DE" dark depressed style="font-weight:700;" @click="$emit('input', false)") {{ $t('GENERAL.CONFIRM') }}
        v-snackbar(v-model="saveErrorVisible" color="error" timeout="3500") {{ $t('WORK_AGENT.ERROR_SHARE_SAVE_FAILED') }}
</template>

<script>
import Vue from 'vue';
import PersonalSettingService from '@/services/PersonalSettingService';
import PermissionService from '@/services/PermissionService';
import UpGPTPortalDataServices from '@/services/UpGPTPortalDataServices';

const AVATAR_COLORS = ['#2C4A7C', '#7B4EA6', '#1A7A5C', '#C4652A', '#2A7F8F', '#8F2A6E', '#5C6E1E', '#7A3030'];

export default Vue.extend({
    name: 'WorkAgentAccessDialog',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        entityType: {
            type: String,
            required: true,
            validator: (v) => ['TEMPLATE', 'INSTANCE'].includes(v),
        },
        entityId: {
            type: String,
            required: true,
        },
        entityName: {
            type: String,
            default: '',
        },
        entityOwner: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isPublic: false,
            selectedUsers: [],
            selectedGroups: [],
            userList: [],
            groupList: [],
            ownerId: null,           // 實體 owner 的 user id；從「分享給使用者」下拉排除
            loadingData: false,
            saveErrorVisible: false,
            // 即時儲存狀態
            saveState: '',          // '' | 'saving' | 'saved'
            suppressAutoSave: false, // 初次載入時抑制自動儲存，避免迴圈
            dirty: false,            // 本次開啟期間是否曾成功儲存（決定關閉時是否通知父層）
            pendingSave: false,      // 是否有等待 debounce 的變更
            saveTimer: null,
        };
    },
    computed: {
        entityTypeLabel() {
            return this.entityType === 'TEMPLATE'
                ? this.$t('WORK_AGENT.ENTITY_TYPE_TEMPLATE')
                : this.$t('WORK_AGENT.ENTITY_TYPE_INSTANCE');
        },
        // 排除 owner 本人（owner 天生有存取權，後端 VAL-010 也會忽略寫入 owner）
        selectableUsers() {
            if (this.ownerId == null) return this.userList;
            return this.userList.filter(u => u.id !== this.ownerId);
        },
        selectAllUsersIcon() {
            if (this.selectableUsers.length > 0 && this.selectedUsers.length === this.selectableUsers.length) return 'mdi-checkbox-marked';
            if (this.selectedUsers.length > 0) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        selectAllGroupsIcon() {
            if (this.groupList.length > 0 && this.selectedGroups.length === this.groupList.length) return 'mdi-checkbox-marked';
            if (this.selectedGroups.length > 0) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.initData();
            } else {
                this.onClosed();
            }
        },
        isPublic() {
            this.scheduleAutoSave();
        },
        selectedUsers: {
            deep: true,
            handler() { this.scheduleAutoSave(); },
        },
        selectedGroups: {
            deep: true,
            handler() { this.scheduleAutoSave(); },
        },
    },
    beforeDestroy() {
        clearTimeout(this.saveTimer);
    },
    methods: {
        async initData() {
            // 重置狀態並抑制載入期間的自動儲存
            this.suppressAutoSave = true;
            this.saveState = '';
            this.dirty = false;
            this.pendingSave = false;
            this.ownerId = null;
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
            this.loadingData = true;
            await Promise.all([this.loadUserList(), this.loadGroupList()]);
            await this.loadCurrentAccess();
            this.loadingData = false;
            // $nextTick 在 watcher callback flush 之後才執行，確保載入造成的變更不會觸發儲存
            this.$nextTick(() => { this.suppressAutoSave = false; });
        },
        async loadUserList() {
            try {
                const response = await PersonalSettingService.getUpGPTUserDetail();
                const raw = response.data.userList || [];
                this.userList = raw
                    .filter(u => u.is_active)
                    .map(u => ({ id: u.id, name: u.last_name + ' ' + u.first_name, email: u.email || '' }));
            } catch (e) {
                // ignore — list stays empty
            }
        },
        async loadGroupList() {
            try {
                const response = await PermissionService.getUpGPTGroups();
                const raw = Array.isArray(response.data) ? response.data : [];
                this.groupList = raw
                    .filter(g => g.permission_type === 'UPGPT')
                    .map(g => ({
                        id: Number(g.id),
                        name: g.name,
                        memberCount: Array.isArray(g.user_set) ? g.user_set.length : null,
                    }));
            } catch (e) {
                // ignore
            }
        },
        async loadCurrentAccess() {
            try {
                let response;
                if (this.entityType === 'TEMPLATE') {
                    response = await UpGPTPortalDataServices.getTemplateAccess(this.entityId);
                } else {
                    response = await UpGPTPortalDataServices.getInstanceAccess(this.entityId);
                }
                const data = response.data;
                this.isPublic = !!data.is_public;
                this.ownerId = data.owner_id ?? null;
                const userShares = Array.isArray(data.user_shares) ? data.user_shares : [];
                const groupShares = Array.isArray(data.group_shares) ? data.group_shares : [];
                this.selectedUsers = userShares.map(u => u.id);
                this.selectedGroups = groupShares.map(g => g.id);
            } catch (e) {
                // leave defaults
            }
        },
        removeUser(item) {
            const idx = this.selectedUsers.indexOf(item.id);
            if (idx !== -1) this.selectedUsers.splice(idx, 1);
        },
        removeGroup(item) {
            const idx = this.selectedGroups.indexOf(item.id);
            if (idx !== -1) this.selectedGroups.splice(idx, 1);
        },
        scheduleAutoSave() {
            if (this.suppressAutoSave) return;
            this.pendingSave = true;
            clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => { this.flushSave(); }, 600);
        },
        async flushSave() {
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
            if (!this.pendingSave) return;
            this.pendingSave = false;
            await this.autoSave();
        },
        async autoSave() {
            this.saveState = 'saving';
            try {
                const payload = {
                    is_public: this.isPublic,
                    user_ids: this.selectedUsers,
                    group_ids: this.selectedGroups,
                };
                if (this.entityType === 'TEMPLATE') {
                    await UpGPTPortalDataServices.saveTemplateAccess(this.entityId, payload);
                } else {
                    await UpGPTPortalDataServices.saveInstanceAccess(this.entityId, payload);
                }
                this.dirty = true;
                // 儲存成功後保留「已儲存」狀態於畫面上，讓使用者確認儲存成功（不自動消失）
                this.saveState = 'saved';
            } catch (e) {
                this.saveState = '';
                this.saveErrorVisible = true;
                // 儲存失敗：以伺服器資料重新同步 UI，避免顯示未儲存的狀態
                this.suppressAutoSave = true;
                await this.loadCurrentAccess();
                this.$nextTick(() => { this.suppressAutoSave = false; });
            }
        },
        async onClosed() {
            // 關閉前 flush 尚未送出的 debounce 變更
            if (this.saveTimer || this.pendingSave) {
                await this.flushSave();
            }
            // 通知父層更新列表 icon（公開狀態）
            if (this.dirty) {
                this.$emit('updated', { id: this.entityId, isPublic: this.isPublic });
            }
            this.dirty = false;
            this.saveState = '';
        },
        toggleAllUsers() {
            if (this.selectedUsers.length === this.selectableUsers.length) {
                this.selectedUsers = [];
            } else {
                this.selectedUsers = this.selectableUsers.map(u => u.id);
            }
        },
        toggleAllGroups() {
            if (this.selectedGroups.length === this.groupList.length) {
                this.selectedGroups = [];
            } else {
                this.selectedGroups = this.groupList.map(g => g.id);
            }
        },
        filterUsers(item, queryText) {
            const q = queryText.toLowerCase();
            return item.name.toLowerCase().includes(q) || item.email.toLowerCase().includes(q);
        },
        userInitial(name) {
            return name ? name.trim().charAt(0).toUpperCase() : '?';
        },
        avatarColor(id) {
            return AVATAR_COLORS[id % AVATAR_COLORS.length];
        },
    },
});
</script>

<style scoped>
/* Visibility cards */
.visibility-card {
    cursor: pointer;
    border-radius: 8px;
    border: 1.5px solid #E4E4E0;
    padding: 12px;
    transition: border-color 0.2s, background-color 0.2s;
    background: #fff;
    height: 100%;
}
.visibility-card:hover {
    border-color: #14C6DE;
}
.visibility-card--active {
    border-color: #14C6DE;
    background-color: #F0FCFD;
}

/* Radio circle */
.radio-circle {
    width: 16px;
    height: 16px;
    min-width: 16px;
    border-radius: 50%;
    border: 2px solid #BDBDBD;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s;
}
.radio-circle--active {
    border-color: #14C6DE;
}
.radio-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #14C6DE;
}

/* Share input container */
.share-input {
    border: 1px solid #E4E4E0 !important;
    border-radius: 8px !important;
    background: #fff !important;
}
.share-input .v-input__control {
    min-height: 44px !important;
}
.share-input.v-text-field.v-text-field--solo .v-input__control {
    min-height: 44px !important;
}
/* Vuetify 預設對 dense 非 outlined input 只給 padding: 4px 0 2px，
   在 44px 容器內 placeholder 會被擠在頂端而像被切掉。
   僅針對 share-input 覆寫上下 padding 為 10px，使其垂直置中。 */
::v-deep .share-input input {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
}

/* Chip border, background, text color */
::v-deep .share-chip.v-chip {
    border: 1px solid var(--grey-300, #CCC) !important;
    background-color: #fff !important;
    color: #666 !important;
    box-shadow: none !important;
}

/* Close icon color to match Close.svg #666 */
::v-deep .share-chip .v-chip__close.v-icon {
    color: #666666 !important;
}

/* Info strip */
.info-strip {
    display: flex;
    align-items: flex-start;
    background: #F5F5F5;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 12px;
    color: #757575;
    line-height: 1.5;
}

/* Dropdown user item avatar */
.user-item-avatar {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
}

/* User avatar circle inside chip */
.user-avatar {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
}
</style>

<!-- Global: autocomplete menu is teleported out of this component's DOM, so scoped ::v-deep cannot reach it. Target the contentClass instead. -->
<style>
.share-menu .v-list-item {
    min-height: 44px;
}
.share-menu .v-list-item__action {
    min-width: 0;
    margin: 0 12px 0 0;
    align-self: center;
}
.share-menu .v-list-item__title {
    font-size: 14px;
}
.share-menu .v-list-item__subtitle {
    font-size: 12px;
}
</style>
